/**
 * lightbox
 *
 * .ist-js__lightbox の子要素に1つ画像がある場合、
 * 画像をクリックするとライトボックスが開きます
 *
 * data-lightbox-caption 属性を使用すると、
 * 画像の下にキャプションを表示できます
 *
 * picture タグが使用される可能性があることに注意してください
 *
 * 使用例
 * document.addEventListener('DOMContentLoaded', () => {
 *   new Lightbox(); // デフォルトセレクターを使用
 *   // または
 *   new Lightbox('.custom-lightbox'); // カスタムセレクターを指定
 * });
 *
 * <a href="image.jpg" class="ist-js__lightbox" data-lightbox-caption="キャプション">
 *   <img src="image.jpg" alt="画像">
 * </a>
 */

class Lightbox {
  private static dialog: HTMLDialogElement | null = null;
  private containers: HTMLElement[];
  private images: Map<HTMLElement, HTMLImageElement | HTMLPictureElement>;
  private static readonly DIALOG_OPEN_CLASS = 'ist-lightbox--open';

  /**
   * コンストラクター
   * @param elementSelector - ライトボックス要素のセレクター
   */
  constructor(elementSelector: string = '.ist-js__lightbox') {
    const elements = document.querySelectorAll<HTMLElement>(elementSelector);
    if (!elements.length) {
      console.warn('ライトボックス要素が見つかりません');
      this.containers = [];
      this.images = new Map();
      return;
    }

    this.containers = Array.from(elements);
    this.images = new Map();

    this.containers.forEach((container) => {
      const picture = container.querySelector<HTMLPictureElement>('picture');
      const img = picture || container.querySelector<HTMLImageElement>('img');
      if (!img) {
        console.warn('ライトボックス内に画像要素が見つかりません: ', container);
        return; // 次のコンテナに進む
      }
      this.images.set(container, img);
    });

    this.init();
  }

  /**
   * 初期化
   */
  private init(): void {
    if (!Lightbox.dialog) {
      const dialogHtml = `
            <dialog class="lightbox__dialog">
                <div class="lightbox__content">
                    <button type="button" class="lightbox__close" aria-label="ダイアログを閉じる"></button>
                    <div class="lightbox__image"></div>
                    <div class="lightbox__caption"></div>
                </div>
            </dialog>
        `;
      document.body.insertAdjacentHTML('beforeend', dialogHtml);
      Lightbox.dialog =
        document.querySelector<HTMLDialogElement>('.lightbox__dialog');
      if (!Lightbox.dialog) {
        console.error('ダイアログ要素が見つかりません');
        return; // ダイアログがない場合でも初期化を継続
      }

      Lightbox.dialog.addEventListener('click', (e) => {
        if (e.target === Lightbox.dialog) {
          this.close();
        }
      });

      Lightbox.dialog.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.close();
      });

      Lightbox.dialog
        .querySelector('.lightbox__close')
        ?.addEventListener('click', () => this.close());
    }

    this.containers.forEach((container) => {
      if (container.tagName.toLowerCase() === 'a') {
        container.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.open(container);
          return false;
        });

        container.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            this.open(container);
            return false;
          }
        });

        container.setAttribute('role', 'button');
        container.setAttribute('tabindex', '0');
        container.setAttribute('aria-haspopup', 'dialog');
      } else {
        container.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.open(container);
        });

        container.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            this.open(container);
          }
        });
      }
    });
  }

  /**
   * ダイアログ内のフォーカス可能な要素を取得
   * @param container - ダイアログコンテナ
   * @returns フォーカス可能な要素の配列
   */
  private getFocusableElements(container: HTMLElement): HTMLElement[] {
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input[type="text"]:not([disabled])',
      'input[type="radio"]:not([disabled])',
      'input[type="checkbox"]:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ];
    return Array.from(
      container.querySelectorAll(focusableSelectors.join(','))
    ) as HTMLElement[];
  }

  /**
   * フォーカストラップの設定
   * @param dialog - ダイアログ要素
   */
  private trapFocus(dialog: HTMLDialogElement): void {
    const focusableElements = this.getFocusableElements(dialog);
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    dialog.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey && document.activeElement === firstElement) {
          // Shift+Tab で最初の要素から前に行こうとした場合、最後の要素にフォーカス
          e.preventDefault();
          lastElement?.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          // Tab で最後の要素から次に行こうとした場合、最初の要素にフォーカス
          e.preventDefault();
          firstElement?.focus();
        }
      }
    });
  }

  /**
   * ライトボックスを開く
   * @param container - ライトボックスを開く要素
   */
  private open(container: HTMLElement): void {
    if (!Lightbox.dialog) {
      console.warn('ダイアログ要素が存在しないため、処理を中断します');
      return;
    }

    const imageElement = this.images.get(container);
    if (!imageElement) {
      console.warn('対応する画像が見つかりません: ', container);
      return;
    }

    const clonedElement = imageElement.cloneNode(true) as HTMLElement;
    const dialogImageContainer =
      Lightbox.dialog.querySelector('.lightbox__image');
    if (!dialogImageContainer) {
      console.error('ダイアログの画像コンテナが見つかりません');
      return;
    }

    // ダイアログ内の画像要素を更新
    dialogImageContainer.innerHTML = '';
    dialogImageContainer.appendChild(clonedElement);

    // キャプションの処理を追加
    const caption = container.getAttribute('data-lightbox-caption');
    const captionContainer =
      Lightbox.dialog.querySelector<HTMLElement>('.lightbox__caption');

    if (captionContainer) {
      if (caption) {
        captionContainer.textContent = caption;
        captionContainer.style.display = 'block';
      } else {
        captionContainer.textContent = '';
        captionContainer.style.display = 'none';
      }
    }

    (Lightbox.dialog as any).activeImage = imageElement;

    // スクロールバーを残してスクロールを無効化
    const scrollBarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollBarWidth}px`;

    // ダイアログを開く
    Lightbox.dialog.showModal();

    // アニメーション用にクラスを追加
    setTimeout(() => {
      document.body.classList.add(Lightbox.DIALOG_OPEN_CLASS);
    }, 50);

    // フォーカストラップを設定
    this.trapFocus(Lightbox.dialog);

    (Lightbox.dialog.querySelector('.lightbox__close') as HTMLElement)?.focus();
  }

  /**
   * ライトボックスを閉じる
   */
  private close(): void {
    if (!Lightbox.dialog) return;

    const activeImage = (Lightbox.dialog as any).activeImage as HTMLElement;

    document.body.classList.remove(Lightbox.DIALOG_OPEN_CLASS);

    setTimeout(() => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';

      Lightbox.dialog?.close();

      // フォーカスを元の要素に戻す
      if (activeImage) {
        activeImage.focus();
      }
    }, 150);
  }
}

export default Lightbox;
